import { useQuery } from '@tanstack/react-query';
import { unwrap } from '@truecare/api-client';
import { api } from '../api';
import { useCurrentFacility } from './use-current-facility';
import { useCurrentHomecare } from './use-current-homecare';

/** The `/auth/me` payload carried in the route context by the /me + /profile
 *  loaders. Only the fields both routes read are typed here. */
export interface MeContext {
  email: string;
  displayName?: string | null;
  roles: { persona: string }[];
}

/**
 * Shared persona detection + profile data-loading for the signed-in portal
 * surface. Both `/me` (working tabs) and `/profile` (personal account) branch
 * on the same persona and reuse the same queries — this keeps that logic in one
 * place so the two routes can never drift.
 */
export function usePersonaHome(me: MeContext) {
  const isCaregiver = me.roles.some((r) => r.persona === 'CAREGIVER');
  const isFacilityUser = me.roles.some((r) => r.persona === 'FACILITY_USER');
  const isHomecareClient = me.roles.some((r) => r.persona === 'HOMECARE_CLIENT_USER');
  const isOperator = me.roles.some((r) => r.persona === 'OPERATOR');

  const caregiverQuery = useQuery({
    queryKey: ['caregiver', 'me'],
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/profile')),
    enabled: isCaregiver,
    retry: false,
  });
  const facilityQuery = useCurrentFacility({ enabled: isFacilityUser });
  const homecareQuery = useCurrentHomecare({ enabled: isHomecareClient });

  return {
    isCaregiver,
    isFacilityUser,
    isHomecareClient,
    isOperator,
    caregiverQuery,
    facilityQuery,
    homecareQuery,
  };
}
